import React, { useEffect, useState, useRef, useCallback } from "react";
import { withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { selectDirectMailCampaignStatsFor } from 'data/campaigns/selectors';
import { getDirectMailCampaignStats } from 'data/campaigns/actions';

import { selectCampaign } from 'data/campaigns/selectors';
import { getMarketingCampaignOverviewId } from 'app/routes';
import { useToast } from "app/components/ToastMessage/ToastManager";

import css from "./style.scss";
import LetterSVG from 'assets/images/letter.svg';
import MailSVG from 'assets/images/mail.svg';
import ClockSVG from 'assets/images/clock.svg';
import CheckSquareSVG from 'assets/images/check-square.svg';
import RefreshSVG from 'assets/images/refresh.svg';
import RightArrow from 'assets/images/arrow-right-white.svg';

// Guards against duplicate mounts of this component (overlapping, non-exclusive route matches
// in app/routes.js can currently render the campaigns page more than once for the same URL —
// see the equivalent guard in ../../DirectMail/index.js). Stopgap: only the first instance to
// mount fetches/renders; any other stays inert for its whole lifetime (it does not retroactively
// activate). A new instance mounted after the active one unmounts can claim the slot instead.
let _activeDirectMailStatsInstance = null;

const DirectMailCampaignStats = ({ history, location }) => {
  const dispatch = useDispatch();
  const showToast = useToast();

  const currentMarketingCampaign = useSelector(selectCampaign);
  const currentCampaignId = currentMarketingCampaign?.get("id");
  // Only the stats that were requested for the campaign on screen count; for any other campaign
  // (just switched, still loading, failed) this is null and the cards show "—".
  const stats = useSelector((state) => selectDirectMailCampaignStatsFor(state, currentCampaignId));

  // Stats are requested only on the campaign's overview page, and only once the campaign loaded into
  // the store is the one in the URL: while a switched-to campaign is still loading, the store keeps
  // the previous one, and asking for its stats would be a wasted (and then stale) request.
  const overviewCampaignId = getMarketingCampaignOverviewId(location.pathname);
  const campaignId = overviewCampaignId !== null && overviewCampaignId === currentCampaignId ? currentCampaignId : null;

  const instanceRef = useRef(null);
  if (instanceRef.current === null) {
    instanceRef.current = {};
  }

  const [isActiveInstance] = useState(() => {
    if (_activeDirectMailStatsInstance === null) {
      _activeDirectMailStatsInstance = instanceRef.current;
      return true;
    }

    return false;
  });

  useEffect(() => {
    return () => {
      if (_activeDirectMailStatsInstance === instanceRef.current) {
        _activeDirectMailStatsInstance = null;
      }
    };
  }, []);

  // Fetches only when this is the active instance and `campaignId` is set, i.e. the user is on (or
  // arrives at) the base Marketing Campaign overview URL of the campaign that is loaded, not when
  // navigating within a sub-campaign page (email/direct mail/dialer/etc.) that renders inside this
  // same component without unmounting it. Coming back to the overview requests fresh numbers.
  useEffect(() => {
    if (!isActiveInstance || campaignId === null) return;

    dispatch(getDirectMailCampaignStats(campaignId)).catch(() => {
      showToast("Something went wrong loading the Direct Mail stats. Please try again.", "error");
    });
  }, [campaignId, isActiveInstance]);

  // Derived on every render, not copied into state, so the cards can never lag behind the store.
  const statsItems = [
    {
      icon: MailSVG,
      metric: stats?.totalPiecesMailed,
      descriptorText: "Pieces Mailed"
    },
    {
      icon: ClockSVG,
      metric: stats?.ordersScheduled,
      descriptorText: "Scheduled"
    },
    {
      icon: CheckSquareSVG,
      metric: stats?.ordersCompleted,
      descriptorText: "Completed"
    },
    {
      icon: RefreshSVG,
      metric: stats?.totalRTS,
      descriptorText: "Return to Sender"
    }
  ];

  const formatNumberWithCommaThousandSeparator = (num) => {
    if (!num || isNaN(num)) return num;
    return Number(num).toLocaleString('en-US');
  }

  if (!isActiveInstance) return null;

  return (
    <>
        <div className={css.directMailStats}>
          <div className={css.header}>
            <div className={css.headerLeftSide}>
              <div className={css.headerIcon}>
                <img src={LetterSVG} />
              </div>

              <div className={css.headerText}>
                Direct Mail
              </div>
            </div>

            <button
              className={css.viewDetailsButton}
              onClick={() => history.push(`/campaign/${currentMarketingCampaign.get("id")}/directmail`)}
            >
              <span className={css.buttonText}>View Details</span>
              <img src={RightArrow} />
            </button>
          </div>

          <div className={css.body}>
            <div className={css.bodyContent}>
              {statsItems?.map((item) =>
                <div className={css.bodyItem}>
                  <div className={css.itemIcon}>
                    <img src={item.icon} />
                  </div>

                  <div className={css.itemContent}>
                    <div className={css.itemMetric}>
                      {item.metric == null ? "—" : formatNumberWithCommaThousandSeparator(item.metric)}
                    </div>
                    <div className={css.itemDescriptor}>
                      {item.descriptorText}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    </>
  );
};

export default withRouter(DirectMailCampaignStats);
